import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import rosteringApi from "store/services/rostering.services";
import {
  applyFlatWorkersToGroupedOptions,
  buildGroupedCommonWorkerOptions,
  extractCommonResourceRecords,
  extractCommonResourceWorkerLists,
  flattenGroupedWorkerOptions,
  getParticipantUuid,
  getWorkerUuid,
  isActiveParticipantStatus,
  mapSilHomeParticipantToOption,
} from "./advanceModeSplitDefaults";
import {
  buildAdvanceNearbyFetchKey,
  enhanceWorkerOptionsWithNearbyDistance,
  fetchNearbyWorkersForIds,
} from "./advanceNearbyWorkersApi";
import {
  isRequestCancelled,
  replaceAbortController,
} from "Utils/requestCancel";

const COMMON_RESOURCES_DEBOUNCE_MS = 400;

/**
 * First non-empty side (participants or workers) becomes the anchor.
 * Anchor side keeps full options; the other side is limited to common-resources API results.
 * When participant anchor returns workers, nearby-workers is called with those UUIDs.
 */
const useAdvanceCommonResources = ({
  isAdvanceMode,
  watch,
  setValue,
  participantSelectionKey = "",
  workerSelectionKey = "",
  sortParticipantsByName,
  sortWorkersByName,
  shiftLocation = null,
  initialAnchor = null,
  preserveAssignedWorkers = false,
}) => {
  const requestRef = useRef(0);
  const nearbyRequestRef = useRef(0);
  const abortControllerRef = useRef(null);
  const nearbyAbortControllerRef = useRef(null);
  const anchorRef = useRef(null);
  const lastFetchKeyRef = useRef("");
  const lastNearbyFetchKeyRef = useRef("");
  const debounceTimerRef = useRef(null);
  const sortParticipantsRef = useRef(sortParticipantsByName);
  const sortWorkersRef = useRef(sortWorkersByName);
  const shiftLocationRef = useRef(shiftLocation);

  shiftLocationRef.current = shiftLocation;
  sortParticipantsRef.current = sortParticipantsByName;
  sortWorkersRef.current = sortWorkersByName;

  const [anchor, setAnchor] = useState(null);
  const [commonWorkerOptions, setCommonWorkerOptions] = useState(null);
  const [commonWorkerGroupedOptions, setCommonWorkerGroupedOptions] = useState(null);
  const [commonParticipantOptions, setCommonParticipantOptions] = useState(null);
  const [commonResourcesLoading, setCommonResourcesLoading] = useState(false);
  const [nearbyWorkers, setNearbyWorkers] = useState([]);
  const [nearbyWorkersLoading, setNearbyWorkersLoading] = useState(false);

  const resetCommonResources = useCallback(() => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    anchorRef.current = null;
    lastFetchKeyRef.current = "";
    lastNearbyFetchKeyRef.current = "";
    requestRef.current += 1;
    nearbyRequestRef.current += 1;
    try {
      abortControllerRef.current?.abort?.();
      nearbyAbortControllerRef.current?.abort?.();
    } catch {
      // ignore
    }
    setAnchor(null);
    setCommonWorkerOptions(null);
    setCommonWorkerGroupedOptions(null);
    setCommonParticipantOptions(null);
    setCommonResourcesLoading(false);
    setNearbyWorkers([]);
    setNearbyWorkersLoading(false);
  }, []);

  const filterSelectedWorkers = useCallback(
    (options) => {
      const list = Array.isArray(options) ? options : [];
      const allowed = new Set(list.map((w) => getWorkerUuid(w)).filter(Boolean));
      const allowedIds = new Set(
        list.map((w) => (w?.id != null ? String(w.id) : null)).filter(Boolean)
      );

      if (preserveAssignedWorkers) {
        (watch("advance_carers") || []).forEach((row) => {
          const uuid = getWorkerUuid(row?.worker);
          if (uuid) allowed.add(uuid);
          if (row?.worker?.id != null) allowedIds.add(String(row.worker.id));
        });
      }

      const current = watch("advance_selected_workers") || [];
      const filtered = current.filter((w) => {
        const uuid = getWorkerUuid(w);
        if (uuid && allowed.has(uuid)) return true;
        // Keep draft stubs that match a loaded worker by numeric id
        if (w?.id != null && allowedIds.has(String(w.id))) return true;
        return false;
      });
      if (filtered.length !== current.length) {
        setValue("advance_selected_workers", filtered, { shouldDirty: true });
      }
    },
    [preserveAssignedWorkers, setValue, watch]
  );

  const filterSelectedParticipants = useCallback(
    (options) => {
      const allowed = new Set(options.map((p) => getParticipantUuid(p)));
      const current = watch("advance_selected_participants") || [];
      const filtered = current.filter((p) => allowed.has(getParticipantUuid(p)));
      if (filtered.length !== current.length) {
        setValue("advance_selected_participants", filtered, { shouldDirty: true });
      }
    },
    [setValue, watch]
  );

  const runNearbyWorkersFetch = useCallback(async (workers, location) => {
    if (!workers?.length) {
      setNearbyWorkers([]);
      setNearbyWorkersLoading(false);
      return;
    }

    const fetchKey = buildAdvanceNearbyFetchKey(workers, location);
    if (lastNearbyFetchKeyRef.current === fetchKey) {
      return;
    }
    lastNearbyFetchKeyRef.current = fetchKey;

    const workerIds = workers.map((w) => getWorkerUuid(w)).filter(Boolean);
    if (!workerIds.length) {
      setNearbyWorkers([]);
      setNearbyWorkersLoading(false);
      return;
    }

    const requestId = ++nearbyRequestRef.current;
    nearbyAbortControllerRef.current = replaceAbortController(
      nearbyAbortControllerRef.current
    );
    const signal = nearbyAbortControllerRef.current?.signal;
    setNearbyWorkersLoading(true);

    try {
      const results = await fetchNearbyWorkersForIds(location, workerIds, {
        signal,
      });
      if (requestId !== nearbyRequestRef.current) return;
      setNearbyWorkers(results);
    } catch (err) {
      if (isRequestCancelled(err)) return;
      if (requestId === nearbyRequestRef.current) {
        setNearbyWorkers([]);
      }
    } finally {
      if (requestId === nearbyRequestRef.current) {
        setNearbyWorkersLoading(false);
      }
    }
  }, []);

  const runFetch = useCallback(
    async (type, uuids, fetchKey) => {
      if (!uuids.length) return;

      const requestId = ++requestRef.current;
      abortControllerRef.current = replaceAbortController(
        abortControllerRef.current
      );
      const signal = abortControllerRef.current?.signal;
      lastFetchKeyRef.current = fetchKey;
      setCommonResourcesLoading(true);

      try {
        const res = await rosteringApi.postCommonResources(
          { type, uuids },
          { signal }
        );
        if (requestId !== requestRef.current) return;

        const records = extractCommonResourceRecords(res);

        if (type === "participant") {
          const { commonWorkers, allParticipantWorkers } =
            extractCommonResourceWorkerLists(res);
          const grouped = buildGroupedCommonWorkerOptions(
            commonWorkers,
            allParticipantWorkers,
            sortWorkersRef.current
          );
          const workers = flattenGroupedWorkerOptions(grouped);
          setCommonWorkerGroupedOptions(grouped.length ? grouped : []);
          setCommonWorkerOptions(workers);
          setCommonParticipantOptions(null);
          filterSelectedWorkers(workers);
          lastNearbyFetchKeyRef.current = "";
          void runNearbyWorkersFetch(workers, shiftLocationRef.current);
        } else {
          const participants = sortParticipantsRef.current(
            records
              .filter(isActiveParticipantStatus)
              .map(mapSilHomeParticipantToOption)
              .filter(Boolean)
          );
          setCommonParticipantOptions(participants);
          setCommonWorkerOptions(null);
          setCommonWorkerGroupedOptions(null);
          setNearbyWorkers([]);
          lastNearbyFetchKeyRef.current = "";
          filterSelectedParticipants(participants);
        }
      } catch (err) {
        if (isRequestCancelled(err)) return;
        if (requestId !== requestRef.current) return;
        if (type === "participant") {
          setCommonWorkerOptions([]);
          setCommonWorkerGroupedOptions([]);
          setNearbyWorkers([]);
          lastNearbyFetchKeyRef.current = "";
          filterSelectedWorkers([]);
        } else {
          setCommonParticipantOptions([]);
          filterSelectedParticipants([]);
        }
      } finally {
        if (requestId === requestRef.current) {
          setCommonResourcesLoading(false);
        }
      }
    },
    [filterSelectedParticipants, filterSelectedWorkers, runNearbyWorkersFetch]
  );

  const scheduleFetch = useCallback(
    (type, uuids, fetchKey) => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        if (lastFetchKeyRef.current === fetchKey) {
          return;
        }
        runFetch(type, uuids, fetchKey);
      }, COMMON_RESOURCES_DEBOUNCE_MS);
    },
    [runFetch]
  );

  useEffect(() => {
    if (!isAdvanceMode) {
      if (anchorRef.current !== null || lastFetchKeyRef.current !== "") {
        resetCommonResources();
      }
      return;
    }

    const ptUuids = participantSelectionKey
      ? participantSelectionKey.split(",").filter(Boolean)
      : [];
    const wrUuids = workerSelectionKey
      ? workerSelectionKey.split(",").filter(Boolean)
      : [];

    if (ptUuids.length === 0 && wrUuids.length === 0) {
      if (anchorRef.current !== null || lastFetchKeyRef.current !== "") {
        resetCommonResources();
      }
      return;
    }

    let nextAnchor = anchorRef.current;

    if (nextAnchor === null) {
      // On advance-shift edit, force the participant (or worker) side as anchor
      // even when both sides are pre-selected, so common-resources is fetched.
      if (initialAnchor === "participant" && ptUuids.length > 0) {
        nextAnchor = "participant";
      } else if (initialAnchor === "worker" && wrUuids.length > 0) {
        nextAnchor = "worker";
      } else if (ptUuids.length > 0 && wrUuids.length === 0) {
        nextAnchor = "participant";
      } else if (wrUuids.length > 0 && ptUuids.length === 0) {
        nextAnchor = "worker";
      }
    }

    if (nextAnchor === "participant" && ptUuids.length === 0) {
      nextAnchor = wrUuids.length > 0 ? "worker" : null;
      setCommonWorkerOptions(null);
      setCommonWorkerGroupedOptions(null);
      setNearbyWorkers([]);
      lastFetchKeyRef.current = "";
      lastNearbyFetchKeyRef.current = "";
    }

    if (nextAnchor === "worker" && wrUuids.length === 0) {
      nextAnchor = ptUuids.length > 0 ? "participant" : null;
      setCommonParticipantOptions(null);
      lastFetchKeyRef.current = "";
    }

    if (nextAnchor !== anchorRef.current) {
      anchorRef.current = nextAnchor;
      setAnchor(nextAnchor);
      lastFetchKeyRef.current = "";
      lastNearbyFetchKeyRef.current = "";
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
    }

    if (nextAnchor === null) {
      setCommonWorkerOptions(null);
      setCommonWorkerGroupedOptions(null);
      setCommonParticipantOptions(null);
      setNearbyWorkers([]);
      return;
    }

    if (nextAnchor === "participant") {
      setCommonParticipantOptions(null);
      if (ptUuids.length === 0) {
        setCommonWorkerOptions(null);
        setCommonWorkerGroupedOptions(null);
        setNearbyWorkers([]);
        return;
      }
      const fetchKey = `participant:${[...ptUuids].sort().join(",")}`;
      if (lastFetchKeyRef.current === fetchKey) {
        return;
      }
      requestRef.current += 1;
      setCommonWorkerOptions(null);
      setCommonWorkerGroupedOptions(null);
      setNearbyWorkers([]);
      lastNearbyFetchKeyRef.current = "";
      scheduleFetch("participant", ptUuids, fetchKey);
      return;
    }

    if (nextAnchor === "worker") {
      setCommonWorkerOptions(null);
      setCommonWorkerGroupedOptions(null);
      setNearbyWorkers([]);
      lastNearbyFetchKeyRef.current = "";
      if (wrUuids.length === 0) {
        setCommonParticipantOptions(null);
        return;
      }
      const fetchKey = `worker:${[...wrUuids].sort().join(",")}`;
      if (lastFetchKeyRef.current === fetchKey) {
        return;
      }
      setCommonParticipantOptions(null);
      scheduleFetch("worker", wrUuids, fetchKey);
    }
  }, [
    isAdvanceMode,
    participantSelectionKey,
    workerSelectionKey,
    resetCommonResources,
    scheduleFetch,
    initialAnchor,
  ]);

  // Re-fetch nearby-workers when shift location arrives after common-resources workers
  useEffect(() => {
    if (!isAdvanceMode || !commonWorkerOptions?.length) return;
    void runNearbyWorkersFetch(commonWorkerOptions, shiftLocation);
  }, [
    isAdvanceMode,
    commonWorkerOptions,
    shiftLocation?.lat,
    shiftLocation?.lng,
    shiftLocation?.street,
    shiftLocation?.suburb,
    shiftLocation?.postcode,
    shiftLocation?.state_name,
    shiftLocation?.state,
    runNearbyWorkersFetch,
  ]);

  const commonWorkerOptionsWithDistance = useMemo(() => {
    if (!commonWorkerOptions?.length) return commonWorkerOptions;
    if (!nearbyWorkers.length) return commonWorkerOptions;
    return enhanceWorkerOptionsWithNearbyDistance(
      commonWorkerOptions,
      nearbyWorkers
    );
  }, [commonWorkerOptions, nearbyWorkers]);

  const commonWorkerGroupedOptionsWithDistance = useMemo(() => {
    if (commonWorkerGroupedOptions === null) return null;
    if (!commonWorkerGroupedOptions.length) return commonWorkerGroupedOptions;
    if (!commonWorkerOptionsWithDistance?.length) {
      return commonWorkerGroupedOptions;
    }
    return applyFlatWorkersToGroupedOptions(
      commonWorkerGroupedOptions,
      commonWorkerOptionsWithDistance
    );
  }, [
    commonWorkerGroupedOptions,
    commonWorkerOptionsWithDistance,
  ]);

  useEffect(
    () => () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      requestRef.current += 1;
      nearbyRequestRef.current += 1;
      try {
        abortControllerRef.current?.abort?.();
        nearbyAbortControllerRef.current?.abort?.();
      } catch {
        // ignore
      }
    },
    []
  );

  return {
    resetCommonResources,
    commonResourcesLoading,
    commonWorkerOptions,
    commonWorkerOptionsWithDistance,
    commonWorkerGroupedOptions,
    commonWorkerGroupedOptionsWithDistance,
    commonParticipantOptions,
    commonAnchor: anchor,
    nearbyWorkersLoading,
    nearbyWorkers,
  };
};

export default useAdvanceCommonResources;
